import type { SoundKey } from "../assets";

/** The minimal sound-instance surface the pool needs (Phaser sounds satisfy it). */
export interface Voice {
	readonly isPlaying: boolean;
	play(): boolean;
	stop(): boolean;
	/** Stereo pan (-1 … 1), where the platform supports it. */
	setPan?(value: number): unknown;
}

/**
 * Voices per sound: 15 in total, like the original's 15 SDL_mixer channels
 * (GRAVITY_NUM_CHANNELS). Unlike the original, sounds don't share channels, so a held
 * laser (one shot per tick) can never silence explosions.
 */
export const VOICES_PER_SOUND: Record<SoundKey, number> = {
	laser: 4,
	missile: 3,
	explosion: 6,
	boing: 2,
	pickup: 2,
	/** Engines loop, one per ship (EngineMixer), outside the pool. */
	engine: 0,
};

/** Important sounds restart their oldest voice when all are busy; the rest are dropped. */
const RESTART_WHEN_FULL: ReadonlySet<SoundKey> = new Set(["explosion", "boing", "pickup"]);

export class VoicePool {
	private readonly voices = new Map<SoundKey, Voice[]>();
	private readonly nextToSteal = new Map<SoundKey, number>();

	constructor(create: (key: SoundKey) => Voice | null, sizes = VOICES_PER_SOUND) {
		for (const [key, count] of Object.entries(sizes) as [SoundKey, number][]) {
			const list: Voice[] = [];
			for (let i = 0; i < count; i++) {
				const voice = create(key);
				if (voice) list.push(voice);
			}
			this.voices.set(key, list);
		}
	}

	/** Plays `key` on a free voice. Returns false if the sound was dropped. */
	play(key: SoundKey, pan = 0): boolean {
		const list = this.voices.get(key);
		if (!list || list.length === 0) return false;

		const free = list.find((v) => !v.isPlaying);
		if (free) {
			free.setPan?.(pan);
			return free.play();
		}
		if (!RESTART_WHEN_FULL.has(key)) return false;

		// Round-robin over busy voices approximates "oldest first".
		const index = (this.nextToSteal.get(key) ?? 0) % list.length;
		this.nextToSteal.set(key, index + 1);
		const voice = list[index];
		if (!voice) return false;
		voice.stop();
		voice.setPan?.(pan);
		return voice.play();
	}

	stopAll(): void {
		for (const list of this.voices.values()) for (const v of list) v.stop();
	}
}
